import * as RadHoverCard from '@radix-ui/react-hover-card'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled, { keyframes } from 'styled-components'

import { findScrollableAncestor } from '@root/shared/lib/dom/find-scrollable-ancestor'
import { mergeRefs, type ClassNameProps } from '@root/shared/react'

import {
  forwardWheelToMarquee,
  normalizeWheelDelta,
  useMarqueeWheelRoot,
} from '../marquee/marquee-wheel'

import {
  TooltipCardParentGuardContext,
  useTooltipCardParentGuardContext,
} from './tooltip-card-parent-guard-context'

const OPEN_DELAY_MS = 100
const CLOSE_DELAY_MS = 50
// Hover-leave closes are owned by the pointer tracking below, not by Radix:
// Chromium on Windows emits pointerleave (`relatedTarget: null`) while the
// cursor is still on the trigger and reopens on the paired enter, flapping the
// card open/closed as the pointer creeps across a small trigger. Radix runs
// leave-driven closes through this timeout while Escape / outside-pointerdown
// dismissals bypass it, so with an unreachable delay every close Radix reports
// is a genuine dismiss and can be applied as-is. Genuine hover-leaves still
// close within CLOSE_DELAY_MS via the pointer-move fallback. Value is the max
// setTimeout delay (~24.8 days); larger values overflow to 0.
const RADIX_CLOSE_DELAY_MS = 2 ** 31 - 1
// Spans trackpad (~16ms between ticks) and mouse-wheel (~200ms) cadences.
const WHEEL_GRACE_IDLE_MS = 250
// Pixel-mode wheel ticks closer than this are treated as one gesture (trackpad
// / momentum). `scrollBy({ behavior: 'smooth' })` on each of those stacks
// competing animations and breaks trackpad feel; use `instant` there only.
const WHEEL_CONTINUOUS_GAP_MS = 55

// Only one tooltip is open at a time; track the active forwarder globally so
// a newly-opening tooltip can tear down a sibling's lingering grace listener
// (otherwise both would forward and double-scroll the same wheel event).
let activeWheelCleanup: (() => void) | undefined

export type TooltipCardAreaProps = {
  closeArea?: React.ReactElement
  clickArea?: React.ReactElement
}

// Shared shape for the trigger/clickArea children we clone: we attach a ref and
// compose our pointer/mouse handlers with any the consumer already supplied.
type ClonableElement = React.ReactElement<{
  ref?: React.Ref<HTMLElement>
  onPointerEnter?: React.PointerEventHandler<HTMLElement>
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
}>

export type TooltipCardProps = {
  children: React.ReactElement<React.RefAttributes<HTMLElement>>
  /** Renders content directly in the portal without the card body chrome. */
  bare?: boolean
  /** When true, close as soon as the pointer enters the close area. */
  closeOnCloseAreaMouseEnter?: boolean
  /** When true, close on close-area enter even inside a suppressing parent TooltipCard. */
  ignoreParentCloseAreaSuppression?: boolean
  /** Passed to nested TooltipCards via the parent guard. @default true */
  nestedSuppressCloseAreaClose?: boolean
  /** Closes when scroll happens outside the tooltip body. */
  closeOnExternalScroll?: boolean
  disableBuiltInWheelForwarding?: boolean
  onOpenChange?: (open: boolean) => void
  /** Keeps open while nested portaled overlays inside are open. */
  respectNestedOverlays?: boolean
  /** Mirrors whether a nested portaled TooltipCard inside is open. */
  nestedOverlayOpenRef?: React.MutableRefObject<boolean>
  contentRef?: React.RefObject<HTMLElement | null>
  open?: boolean
} & ClassNameProps &
  Pick<
    RadHoverCard.HoverCardContentProps,
    'align' | 'side' | 'sideOffset' | 'alignOffset' | 'collisionPadding'
  > &
  TooltipCardAreaProps

export const TooltipCard = ({
  className,
  children,
  closeArea,
  clickArea,
  bare = false,
  side = 'top',
  align = 'center',
  sideOffset = 0,
  alignOffset = 0,
  collisionPadding,
  closeOnCloseAreaMouseEnter = true,
  ignoreParentCloseAreaSuppression = false,
  nestedSuppressCloseAreaClose = true,
  closeOnExternalScroll = true,
  disableBuiltInWheelForwarding = false,
  respectNestedOverlays = true,
  nestedOverlayOpenRef: nestedOverlayOpenRefProp,
  contentRef,
  open: controlledOpen,
  onOpenChange: onOpenChangeProp,
}: TooltipCardProps) => {
  const isControlled = controlledOpen !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const open = isControlled ? controlledOpen : internalOpen
  const bodyRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement>(null)
  const marqueeRoot = useMarqueeWheelRoot()
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const lastWheelTsRef = useRef<number>(-Infinity)
  const openedAtRef = useRef<number>(-Infinity)
  // Pointer position from the two most recent global moves while open. Used by
  // the close-area handler to recover the trigger→close-area passthrough when a
  // stray move in the trigger/body gap clears `relatedTarget` (see below).
  const lastPointerPosRef = useRef<{ x: number; y: number } | undefined>(
    undefined,
  )
  const prevPointerPosRef = useRef<{ x: number; y: number } | undefined>(
    undefined,
  )
  const parentGuard = useTooltipCardParentGuardContext()
  const registeredWithParentGuardRef = useRef(false)

  // Guard exposed to *our own* portal content so nested TooltipCards can
  // register and we keep ourselves open while they're open. This is distinct
  // from `parentGuard` (the enclosing TooltipCard's guard, which we register
  // ourselves into). Reading from `parentGuard` here would conflate our own
  // open state with a nested overlay being open and permanently disable the
  // defensive pointer-move close fallback below.
  const childOverlayOpenCountRef = useRef(0)
  const internalChildOverlayOpenRef = useRef(false)
  const childOverlayOpenRef =
    nestedOverlayOpenRefProp ?? internalChildOverlayOpenRef
  // After closeOnBodyEnter (or the early passthrough close), block Radix from
  // reopening while the pointer is still on the trigger — otherwise the body
  // reappears under the cursor, fires body-enter again, and flickers.
  const registerChildOverlayOpen = useCallback((open: boolean) => {
    if (open) {
      childOverlayOpenCountRef.current += 1
    } else {
      childOverlayOpenCountRef.current = Math.max(
        0,
        childOverlayOpenCountRef.current - 1,
      )
    }
    childOverlayOpenRef.current = childOverlayOpenCountRef.current > 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const childGuardValue = useMemo(
    () => ({
      registerNestedOverlayOpen: registerChildOverlayOpen,
      nestedOverlayOpenRef: childOverlayOpenRef,
      suppressCloseAreaClose: nestedSuppressCloseAreaClose,
      ignoreParentCloseAreaSuppression:
        parentGuard.ignoreParentCloseAreaSuppression,
      cascadePreviewDepth: parentGuard.cascadePreviewDepth,
    }),
    [
      childOverlayOpenRef,
      nestedSuppressCloseAreaClose,
      parentGuard.ignoreParentCloseAreaSuppression,
      parentGuard.cascadePreviewDepth,
      registerChildOverlayOpen,
    ],
  )

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = undefined
    }
  }

  const syncParentGuard = useCallback(
    (next: boolean) => {
      if (next && !registeredWithParentGuardRef.current) {
        parentGuard.registerNestedOverlayOpen(true)
        registeredWithParentGuardRef.current = true
      } else if (!next && registeredWithParentGuardRef.current) {
        parentGuard.registerNestedOverlayOpen(false)
        registeredWithParentGuardRef.current = false
      }
    },
    [parentGuard],
  )

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next)
      }
      if (next) {
        clearCloseTimer()
      }
      syncParentGuard(next)
      onOpenChangeProp?.(next)
    },
    [isControlled, onOpenChangeProp, syncParentGuard],
  )

  // Single source of truth for "the pointer is still on the trigger or the
  // body", shared by every close path. Rect math alone reports the pointer as
  // outside inside the sub-pixel band along an element's edge (layout rects are
  // fractional under Windows display scaling, pointer coordinates are snapped),
  // so fall back to the browser's own hit test — the one that decides where
  // pointer events actually go.
  const isPointerOverSurfaces = useCallback(
    (x: number, y: number) => {
      const trigger = triggerRef.current
      const body = bodyRef.current
      // Radix flips the side on collision (request `top`, render `bottom`).
      // Pad toward the *actual* placement (`data-side`), not the requested one,
      // otherwise the trigger→body transition zone reads as "outside".
      const activeSide = resolveSide(body, side)
      const gapPadding = Math.max(0, sideOffset) + 4
      if (
        isPointerInsideGapExpanded(
          x,
          y,
          trigger,
          activeSide,
          gapPadding,
          'toward-body',
        ) ||
        isPointerInsideGapExpanded(
          x,
          y,
          body,
          activeSide,
          gapPadding,
          'toward-trigger',
        )
      ) {
        return true
      }
      const hit = document.elementFromPoint(x, y)
      return (
        hit !== null &&
        (trigger?.contains(hit) === true || body?.contains(hit) === true)
      )
    },
    [side, sideOffset],
  )

  // Because hover-leave closes never reach Radix (see RADIX_CLOSE_DELAY_MS),
  // any close arriving here is a genuine dismiss (Escape, pointerdown outside).
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
    },
    [setOpen],
  )

  // Keep parent guard in sync with `open` — several close paths call setOpen(false)
  // directly (defensive pointer tracking, scroll, header/body handlers) and skip
  // handleOpenChange, which would otherwise leave nestedOverlayOpenRef stuck true.
  useEffect(() => {
    syncParentGuard(open)
  }, [open, parentGuard, syncParentGuard])

  // Track when the tooltip became open so `handleBodyMouseEnter` can detect the
  // accidental trigger→body passthrough. Driven by `open` (not setOpen) so it
  // also fires in controlled mode, where opening arrives via the `open` prop.
  useEffect(() => {
    openedAtRef.current = open ? performance.now() : -Infinity
  }, [open])

  useEffect(() => {
    return () => {
      if (registeredWithParentGuardRef.current) {
        parentGuard.registerNestedOverlayOpen(false)
        registeredWithParentGuardRef.current = false
      }
    }
  }, [parentGuard])

  const handleBodyMouseEnter = () => {
    // The "<150ms = accidental passthrough" heuristic is wrong for `bare`
    // popovers (e.g. PairPopover): users intentionally hover the trigger and
    // then move onto the body — body-enter is the goal, not a mistake. Skipping
    // it here also keeps the topbar marquee pause from flickering off as soon
    // as the pointer crosses the trigger→body gap.
    if (bare) {
      return
    }
    // Same reasoning for cards with an interactive clickArea (e.g. the dev
    // tokens tooltip's top-tokens list): moving from the trigger onto the body
    // to reach it is intentional. Applying the early-enter close here races the
    // 150ms window against the trigger→body travel time and intermittently
    // dismisses the card before the pointer reaches the clickArea.
    if (clickArea !== undefined) {
      return
    }
  }

  const handleTriggerPointerEnter = (
    event: React.PointerEvent<HTMLElement>,
  ) => {
    ;(
      children as React.ReactElement<{
        onPointerEnter?: React.PointerEventHandler<HTMLElement>
      }>
    ).props.onPointerEnter?.(event)
  }
  const handleCloseOnCloseAreaMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    e.stopPropagation()
    // Nested TooltipCards (opened from a parent tooltip's content) should stay
    // open while the pointer moves within the card, including closeArea ↔
    // clickArea. Layout-level guards (e.g. explore pause) must not suppress
    // this — only the per-portal child guard sets `suppressCloseAreaClose`.
    // Only relevant when there's a clickArea to move to: a passive close-area-
    // only card (e.g. a currency icon preview) has nothing to stay open for, so
    // entering it should still close it.
    if (
      parentGuard.suppressCloseAreaClose &&
      !ignoreParentCloseAreaSuppression &&
      clickArea !== undefined
    ) {
      return
    }
    if (!closeOnCloseAreaMouseEnter) {
      return
    }

    const from = e.relatedTarget
    // Close unless the pointer arrived directly from the trigger (the
    // trigger→close-area passthrough that would otherwise flicker the card
    // open). Checking "not the trigger" rather than "inside the body" also
    // closes when the pointer comes from a portaled nested overlay (e.g. a
    // currency icon preview), whose node lives outside our body in the DOM.
    const trigger = triggerRef.current
    const fromTrigger =
      from instanceof Node && trigger !== null && trigger.contains(from)

    // A fast move from the trigger across the sideOffset gap can land a stray
    // mousemove in the gap, so `relatedTarget` is the gap element rather than
    // the trigger and the passthrough reads as an external entry (which would
    // close the card before the pointer reaches the clickArea). Fall back to
    // the pointer's position just before it entered: if it was on the trigger
    // or within the gap toward the body, treat it as the passthrough.
    const activeSide = resolveSide(bodyRef.current, side)
    const gapPadding = Math.max(0, sideOffset) + 4
    const prevPos = prevPointerPosRef.current
    const fromTriggerGap =
      prevPos !== undefined &&
      isPointerInsideGapExpanded(
        prevPos.x,
        prevPos.y,
        trigger,
        activeSide,
        gapPadding,
        'toward-body',
      )

    if ((!fromTrigger && !fromTriggerGap) || clickArea === undefined) {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (!open) {
      clearCloseTimer()
      return
    }

    // Defensive fallback for cases where Radix's pointerleave is missed
    // (notably on Windows with fast pointer movement or when the pointer
    // leaves the browser window), which otherwise leaves the tooltip stuck
    // open. While open, track the pointer globally and force-close when it
    // is outside both the trigger and the tooltip body.
    const scheduleClose = () => {
      if (closeTimerRef.current) {
        return
      }
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = undefined
        // Final authority before closing: `:hover` is maintained by the same
        // hit test that dispatches pointer events, so it stays true through
        // the bogus leave/enter pairs and sub-pixel rect mismatches that
        // Chromium on Windows produces while the cursor is really on us, and
        // Chromium clears it when the pointer genuinely leaves the window.
        if (
          triggerRef.current?.matches(':hover') === true ||
          bodyRef.current?.matches(':hover') === true
        ) {
          return
        }
        setOpen(false)
      }, CLOSE_DELAY_MS)
    }

    const handlePointerMove = (event: PointerEvent) => {
      prevPointerPosRef.current = lastPointerPosRef.current
      lastPointerPosRef.current = { x: event.clientX, y: event.clientY }
      const inside =
        (respectNestedOverlays && childOverlayOpenRef.current === true) ||
        isPointerOverSurfaces(event.clientX, event.clientY)

      if (inside) {
        clearCloseTimer()
      } else {
        scheduleClose()
      }
    }

    // `relatedTarget: null` usually means the pointer left the window, but
    // Chromium on Windows also reports it while the cursor is still on the
    // trigger — the `:hover` gate in scheduleClose sorts the two apart.
    const handlePointerLeaveWindow = (event: PointerEvent) => {
      if (event.relatedTarget === null) {
        scheduleClose()
      }
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerout', handlePointerLeaveWindow)

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerout', handlePointerLeaveWindow)
      clearCloseTimer()
      lastPointerPosRef.current = undefined
      prevPointerPosRef.current = undefined
    }
  }, [
    childOverlayOpenRef,
    isPointerOverSurfaces,
    open,
    respectNestedOverlays,
    setOpen,
  ])

  useEffect(() => {
    if (!open || !closeOnExternalScroll) {
      return
    }

    const handleScroll = (event: Event) => {
      const target = event.target
      // Scroll originating inside our own portaled body never closes us.
      if (target instanceof Node && bodyRef.current?.contains(target)) {
        return
      }
      // A nested popover is open. Its body is portaled to document root, so its
      // internal scroll reads as "external" here even though it belongs to our
      // subtree — keep open and let the nested overlay manage its own lifetime.
      if (respectNestedOverlays && childOverlayOpenRef.current) {
        return
      }
      // Only close when the scrolled container actually moves our trigger (it
      // contains it). Scrolling an unrelated scroll container elsewhere on the
      // page (e.g. an explore feed auto-scrolling to top) must not close us.
      const trigger = triggerRef.current
      if (trigger && target instanceof Node && !target.contains(trigger)) {
        return
      }
      setOpen(false)
    }

    document.addEventListener('scroll', handleScroll, true)

    return () => {
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [
    childOverlayOpenRef,
    closeOnExternalScroll,
    open,
    respectNestedOverlays,
    setOpen,
  ])

  // The tooltip body is rendered in a portal, so wheel events over it don't
  // reach the trigger's scrollable ancestor. Forward the delta manually, and
  // keep the listener alive for WHEEL_GRACE_IDLE_MS after close-on-scroll fires
  // so the rest of the user's gesture keeps scrolling the parent smoothly.
  // useLayoutEffect (not useEffect) guarantees the listener is armed before
  // paint so the first wheel tick isn't lost.
  useLayoutEffect(() => {
    if (!open || disableBuiltInWheelForwarding) {
      return
    }
    activeWheelCleanup?.()
    lastWheelTsRef.current = -Infinity

    const scrollable = findScrollableAncestor(triggerRef.current)
    if (!scrollable && !bodyContainsScrollLock(bodyRef.current)) {
      return
    }

    let idleTimer: ReturnType<typeof setTimeout> | undefined
    let forwarded = false

    const remove = () => {
      document.removeEventListener('wheel', onWheel, { capture: true })
      if (idleTimer) {
        clearTimeout(idleTimer)
      }
      if (activeWheelCleanup === remove) {
        activeWheelCleanup = undefined
      }
    }

    const onWheel = (event: WheelEvent) => {
      const body = bodyRef.current
      const target = event.target as Node | null
      const inside = !!(body && target && body.contains(target))
      // Let the marquee handle wheels over its own strip (this listener is on
      // document capture and would otherwise run before the marquee listener).
      if (!inside && marqueeRoot && target && marqueeRoot.contains(target)) {
        return
      }

      // While open: only forward wheels over the body. During grace
      // (idleTimer set): forward any wheel so the gesture keeps scrolling.
      if (!inside && !idleTimer) {
        return
      }

      const dy = normalizeWheelDelta(event.deltaY, event.deltaMode)
      const dx = normalizeWheelDelta(event.deltaX, event.deltaMode)
      const scrollLock = body ? findScrollLockTarget(body, event) : undefined
      // Re-checked per event (not captured at setup): body content can mount or
      // unmount a scroll lock while the tooltip is open.
      const bodyHasScrollLock = bodyContainsScrollLock(body)

      if (
        !idleTimer &&
        marqueeRoot &&
        !scrollLock &&
        !bodyHasScrollLock &&
        (dx !== 0 || dy !== 0)
      ) {
        event.preventDefault()
        forwardWheelToMarquee(marqueeRoot, event)
        forwarded = true
        return
      }
      if (scrollLock && !idleTimer) {
        event.preventDefault()
        event.stopImmediatePropagation()
        if (dy !== 0) {
          const maxScroll = scrollLock.scrollHeight - scrollLock.clientHeight
          scrollLock.scrollTop = Math.max(
            0,
            Math.min(maxScroll, scrollLock.scrollTop + dy),
          )
        }
        return
      }
      if (!idleTimer && inside && bodyHasScrollLock) {
        event.preventDefault()
        return
      }
      // Always preventDefault when we apply scroll ourselves; otherwise the
      // browser default scroll still runs (very noticeable as stepped "jumps"
      // on Windows when the ancestor is the document / Window).
      event.preventDefault()

      const ts = event.timeStamp
      const sincePrev = ts - lastWheelTsRef.current
      lastWheelTsRef.current = ts

      // Line/page deltas are almost always a mouse wheel — smooth per notch
      // matches native feel. Pixel deltas in quick succession are trackpad /
      // momentum; smooth on each tick stacks animations and feels broken.
      const behavior: ScrollBehavior =
        event.deltaMode !== 0 || sincePrev >= WHEEL_CONTINUOUS_GAP_MS
          ? 'smooth'
          : 'instant'

      scrollable?.scrollBy({ top: dy, left: dx, behavior })

      forwarded = true
      if (idleTimer) {
        clearTimeout(idleTimer)
        idleTimer = setTimeout(remove, WHEEL_GRACE_IDLE_MS)
      }
    }

    document.addEventListener('wheel', onWheel, {
      passive: false,
      capture: true,
    })
    activeWheelCleanup = remove

    return () => {
      // If the user never wheeled, no gesture to preserve — tear down now.
      if (!forwarded) {
        remove()
        return
      }
      idleTimer = setTimeout(remove, WHEEL_GRACE_IDLE_MS)
    }
  }, [disableBuiltInWheelForwarding, open, marqueeRoot])

  const triggerChild = children as ClonableElement
  // Memoize on the consumer's ref (not the cloned element, which is a fresh
  // object every render) so the merged callback stays stable and React doesn't
  // detach→reattach the trigger ref on every render.
  const triggerChildRef = triggerChild.props.ref
  const mergedTriggerRef = useMemo(
    () => mergeRefs([triggerRef, triggerChildRef]),
    [triggerChildRef],
  )
  const trigger = React.cloneElement(triggerChild, {
    ref: mergedTriggerRef,
    onPointerEnter: handleTriggerPointerEnter,
  })

  const bodyElementRef = useMemo(
    () => mergeRefs([bodyRef, contentRef]) as React.Ref<HTMLDivElement>,
    [contentRef],
  )

  const clickAreaChild = React.isValidElement(clickArea)
    ? (clickArea as ClonableElement)
    : undefined
  const clickAreaChildRef = clickAreaChild?.props.ref
  const mergedClickAreaRef = useMemo(
    () => mergeRefs([bodyElementRef, clickAreaChildRef]),
    [bodyElementRef, clickAreaChildRef],
  )

  const portalContent = bare ? (
    clickAreaChild ? (
      React.cloneElement(clickAreaChild, {
        ref: mergedClickAreaRef,
        onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
          handleBodyMouseEnter()
          clickAreaChild.props.onMouseEnter?.(event)
        },
      })
    ) : (
      <BareBody ref={bodyElementRef} onMouseEnter={handleBodyMouseEnter}>
        {clickArea}
      </BareBody>
    )
  ) : (
    <Body ref={bodyElementRef} onMouseEnter={handleBodyMouseEnter}>
      {closeArea ? (
        <>
          <CloseArea
            onMouseEnter={handleCloseOnCloseAreaMouseEnter}
            data-has-content={clickArea !== undefined}
          >
            {closeArea}
          </CloseArea>
          {clickArea === undefined ? undefined : (
            <ClickArea>{clickArea}</ClickArea>
          )}
        </>
      ) : (
        clickArea
      )}
    </Body>
  )

  if (!closeArea && !clickArea) {
    return trigger
  }

  return (
    <RadHoverCard.Root
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={OPEN_DELAY_MS}
      closeDelay={RADIX_CLOSE_DELAY_MS}
    >
      <RadHoverCard.Trigger asChild>{trigger}</RadHoverCard.Trigger>
      <RadHoverCard.Portal>
        <HoverCardContent
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionPadding={collisionPadding}
          side={side}
          align={align}
          className={className}
        >
          <TooltipCardParentGuardContext.Provider value={childGuardValue}>
            {portalContent}
          </TooltipCardParentGuardContext.Provider>
        </HoverCardContent>
      </RadHoverCard.Portal>
    </RadHoverCard.Root>
  )
}

/**
 * Like `isPointerInside`, but extends the rect along the popover axis to
 * cover the `sideOffset` gap between trigger and body. `direction` selects
 * which edge to extend:
 *   - `'toward-body'`: extend the trigger toward where the popover sits.
 *   - `'toward-trigger'`: extend the body toward where the trigger sits.
 */
type TooltipSide = 'top' | 'right' | 'bottom' | 'left'

// Radix writes the resolved (post-collision) placement to `data-side` on the
// content element, which is an ancestor of the body we hold a ref to. Prefer it
// over the requested side; fall back when the body isn't mounted yet or carries
// an unexpected value.
const resolveSide = (
  el: Element | null,
  fallback: TooltipSide,
): TooltipSide => {
  const dataSide = el?.closest('[data-side]')?.getAttribute('data-side')
  if (
    dataSide === 'top' ||
    dataSide === 'right' ||
    dataSide === 'bottom' ||
    dataSide === 'left'
  ) {
    return dataSide
  }
  return fallback
}

const isPointerInsideGapExpanded = (
  x: number,
  y: number,
  el: Element | null,
  side: TooltipSide,
  pad: number,
  direction: 'toward-body' | 'toward-trigger',
) => {
  if (!el) {
    return false
  }
  const rect = el.getBoundingClientRect()
  let { left, right, top, bottom } = rect
  const expandTowardSide = direction === 'toward-body'
  switch (side) {
    case 'bottom': {
      if (expandTowardSide) {
        bottom += pad
      } else {
        top -= pad
      }
      break
    }
    case 'top': {
      if (expandTowardSide) {
        top -= pad
      } else {
        bottom += pad
      }
      break
    }
    case 'right': {
      if (expandTowardSide) {
        right += pad
      } else {
        left -= pad
      }
      break
    }
    case 'left': {
      if (expandTowardSide) {
        left -= pad
      } else {
        right += pad
      }
      break
    }
  }
  return x >= left && x <= right && y >= top && y <= bottom
}

const bodyContainsScrollLock = (body: HTMLElement | null): boolean =>
  !!body?.querySelector('[data-tooltip-scroll-lock]')

const findScrollLockTarget = (
  body: HTMLElement,
  event: WheelEvent,
): HTMLElement | undefined => {
  // Priority: explicit scroll-lock in the path > explicit scroll-lock nearest
  // the target > a generic `data-vertical-overflow` region. Resolve all three
  // in a single walk of the composed path, remembering the first overflow
  // region as a fallback while we keep scanning for a higher-priority lock.
  let verticalOverflowFallback: HTMLElement | undefined
  for (const node of event.composedPath()) {
    if (!(node instanceof HTMLElement) || !body.contains(node)) {
      continue
    }
    if (node.hasAttribute('data-tooltip-scroll-lock')) {
      return node
    }
    if (
      !verticalOverflowFallback &&
      node.getAttribute('data-vertical-overflow') === 'true'
    ) {
      if (isVerticallyScrollable(node)) {
        verticalOverflowFallback = node
      } else {
        const scroller = node.firstElementChild
        if (
          scroller instanceof HTMLElement &&
          isVerticallyScrollable(scroller)
        ) {
          verticalOverflowFallback = scroller
        }
      }
    }
  }

  const target = event.target
  if (target instanceof Element) {
    const locked = target.closest('[data-tooltip-scroll-lock]')
    if (locked instanceof HTMLElement && body.contains(locked)) {
      return locked
    }
  }

  return verticalOverflowFallback
}

const isVerticallyScrollable = (el: HTMLElement): boolean => {
  const { overflowY } = window.getComputedStyle(el)
  return (
    (overflowY === 'auto' || overflowY === 'scroll') &&
    el.scrollHeight > el.clientHeight
  )
}

const slideDownAndFade = keyframes`
	from {
		opacity: var(--tooltip-opacity-from);
		transform: translateY(-2px);
    scale: var(--tooltip-scale-from);
	}
	to {
		opacity: 1;
		transform: translateY(0);
    scale: 1;
	}
`

const slideUpAndFade = keyframes`
	from {
		opacity: var(--tooltip-opacity-from);
		transform: translateY(2px);
    scale: var(--tooltip-scale-from);
	}
	to {
		opacity: 1;
		transform: translateY(0);
    scale: 1;
	}
`

const slideRightAndFade = keyframes`
	from {
		opacity: var(--tooltip-opacity-from);
		transform: translateX(-2px);
    scale: var(--tooltip-scale-from);
	}
	to {
		opacity: 1;
		transform: translateX(0);
    scale: 1;
	}
`

const slideLeftAndFade = keyframes`
	from {
    opacity: var(--tooltip-opacity-from);
		transform: translateX(2px);
    scale: var(--tooltip-scale-from);
	}
	to {
		opacity: 1;
		transform: translateX(0);
    scale: 1;
	}
`

const HoverCardContent = styled(RadHoverCard.Content)`
  --root-padding: 14px;
  --tooltip-scale-from: 0.8;
  --tooltip-opacity-from: 0.8;

  background: var(--tooltip-background, var(--background-3));
  border-radius: var(--tooltip-border-radius, 12px);
  padding: var(--tooltip-padding, 0);

  color: var(--text-12);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;

  box-shadow:
    hsl(206 22% 7% / 35%) 0px 10px 38px -10px,
    hsl(206 22% 7% / 20%) 0px 10px 20px -15px;

  user-select: none;
  animation-duration: 50ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;

  &[data-state='open'][data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-state='open'][data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-state='open'][data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-state='open'][data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`

const BareBody = styled.div`
  display: contents;
`

const Body = styled.div`
  border: 0.5px solid
    var(--tooltip-card-body-border-color, var(--backgroundA-3));
  background: var(--tooltip-card-body-background, var(--background-3));
  border-radius: var(--tooltip-border-radius, 12px);
  width: var(--tooltip-width, 220px);

  position: relative;
  display: flex;
  flex-direction: column;

  overflow: hidden;
`
const CloseArea = styled.div`
  color: var(--text-12);

  padding: var(--root-padding);

  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  width: 100%;

  &[data-has-content='true'] {
    padding-bottom: 0;
  }
`
const ClickArea = styled.div`
  padding: var(--root-padding);
  padding-top: 0;
`
